import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { message as antdMessage } from "antd";
import { auth } from "../../Config/firebase-config";
import { Logout } from "../../actions/Auth/Auth";
import { usePremiumStatus } from "../../hooks/usePremiumStatus";
import { getCurrentSubscriptionStatus, cancelPaddleSubscriptionAPI } from "../../actions/Billing/Paddle";
import PremiumGateDialog from "../../Components/Common/PremiumGateDialog";
import ConfirmDialog from "../../Components/Common/ConfirmDialog";
import diamond from "../../Assets/Images/icons/diamond.png";
import profileMockBanner from "../../Assets/Images/profile_mock_banner.jpg";
import "../../Assets/Styles/pages/landingRedesign.scss";
import "../../Assets/Styles/pages/profilePage.scss";
import Nav from "../../Components/LandingComponents/Nav";
import type { AppDispatch, RootState } from "../../Redux/store";

const ProfilePage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch<AppDispatch>();
  const isPremium = usePremiumStatus();
  const userSliceState = useSelector((state: RootState) => state.userSlice);

  const [showGateDialog, setShowGateDialog] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Subscription cancelation states
  const [subscriptionSource, setSubscriptionSource] = useState<string>("");
  const [renewalDate, setRenewalDate] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCancelAdminInfo, setShowCancelAdminInfo] = useState(false);
  const [showCancelMobileInfo, setShowCancelMobileInfo] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Check if Firebase has a user or fallback to Redux or Usman Ghani (mock details)
  const firebaseUser = auth.currentUser;
  const isLoggedIn = Boolean(Object.keys(userSliceState.user_Auth || {}).length) || Boolean(firebaseUser);

  const displayName = firebaseUser?.displayName ||
    (userSliceState.user_Auth?.displayName as string) ||
    "Usman Ghani";

  const email = firebaseUser?.email ||
    (userSliceState.user_Auth?.email as string) ||
    "ghani.nisar@gmail.com";

  const photoURL = firebaseUser?.photoURL ||
    (userSliceState.user_Auth?.photoURL as string) ||
    null;

  // Retrieve subscription source on mount
  useEffect(() => {
    if (isLoggedIn) {
      const getStatus = async () => {
        try {
          const status = await getCurrentSubscriptionStatus(true);
          if (status.source) {
            setSubscriptionSource(status.source);
          }
          if (status.renewalDate) {
            setRenewalDate(status.renewalDate);
          }
        } catch (e) {
          console.error("Failed to fetch subscription status:", e);
        }
      };
      getStatus();
    }
  }, [isLoggedIn]);

  const formatRenewalDate = (dateVal: string | null) => {
    if (!dateVal) return "";
    const parsed = Number(dateVal);
    // If it's a valid number, assume seconds if < 10000000000 (year 2286), else ms
    if (!isNaN(parsed)) {
      const ms = parsed < 10000000000 ? parsed * 1000 : parsed;
      return new Date(ms).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    }
    // If it's an ISO string
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  };

  const handleBack = () => {
    history.push("/home");
  };

  const handleLogoutClick = () => {
    dispatch(Logout());
    antdMessage.success("Signed out successfully.");
    history.push("/");
  };

  const handleDeactivateConfirm = () => {
    if (!isLoggedIn) {
      antdMessage.success("Account deactivated successfully (Demo Mode).");
      dispatch(Logout());
      history.push("/");
      return;
    }

    // Since there's no backend deactivation endpoint, log the user out and show success
    antdMessage.success("Your account has been deactivated. You have been signed out.");
    setShowDeactivateConfirm(false);
    dispatch(Logout());
    history.push("/");
  };

  const handleDeleteConfirm = async () => {
    const user = auth.currentUser;
    if (!user) {
      antdMessage.success("Account permanently deleted (Demo Mode).");
      dispatch(Logout());
      history.push("/");
      return;
    }

    setDeleteLoading(true);
    try {
      await user.delete();
      antdMessage.success("Your account has been permanently deleted.");
      dispatch(Logout());
      history.push("/");
    } catch (error: any) {
      console.error("Account deletion error:", error);
      if (error.code === "auth/requires-recent-login") {
        antdMessage.error("For security reasons, please log out and log back in before deleting your account.");
      } else {
        antdMessage.error(error.message || "Failed to delete account. Please try again later.");
      }
    } finally {
      setDeleteLoading(false);
    }
    setShowDeleteConfirm(false);
  };

  const handleCancelSubscriptionClick = () => {
    // If not logged in, simulate paddle cancelation
    if (!isLoggedIn) {
      setShowCancelConfirm(true);
      return;
    }

    const source = subscriptionSource.toLowerCase();

    if (source.includes("admin") || source.includes("manual")) {
      setShowCancelAdminInfo(true);
    } else if (
      source.includes("revenuecat") ||
      source.includes("revenue_cat") ||
      source.includes("google") ||
      source.includes("apple") ||
      source.includes("appstore") ||
      source.includes("playstore") ||
      source.includes("ios") ||
      source.includes("android")
    ) {
      setShowCancelMobileInfo(true);
    } else {
      // Default / Paddle
      setShowCancelConfirm(true);
    }
  };

  const handleCancelSubscriptionConfirm = async () => {
    if (!isLoggedIn) {
      antdMessage.success("Subscription cancelled successfully (Demo Mode).");
      setShowCancelConfirm(false);
      return;
    }

    setCancelLoading(true);
    try {
      const response = await cancelPaddleSubscriptionAPI();
      const successMessage = response?.data?.message || "Your subscription has been cancelled successfully. It will remain active until the end of the billing period.";
      antdMessage.success(successMessage, 6);

      // Refresh status to fetch latest claims
      try {
        await getCurrentSubscriptionStatus(true);
      } catch (refreshErr) {
        console.error("Error refreshing subscription status:", refreshErr);
      }
    } catch (err: any) {
      console.error("Subscription cancellation error:", err);
      antdMessage.error(err.response?.data?.message || err.message || "Failed to cancel subscription. Please contact support.");
    } finally {
      setCancelLoading(false);
      setShowCancelConfirm(false);
    }
  };

  const avatarInitial = displayName?.trim()?.charAt(0)?.toUpperCase() || "U";

  return (
    <>
      {/* .landing-redesign only provides the font-size:16px context Nav's `em` units need --
          it has no height of its own (Nav is position:fixed), so it doesn't affect this
          page's own layout/background below. */}
      <div className="landing-redesign" style={{ position: "static" }}>
        <Nav />
      </div>
      <div className="profile-page-container">
      <div className="profile-card-wrapper">
        {/* Left showcase image panel */}
        <div className="profile-image-panel">
          <button className="profile-back-btn" onClick={handleBack} aria-label="Go Back">
            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 13L1 7L7 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <img src={profileMockBanner} alt="Featured Mockups" className="profile-showcase-image" />
        </div>

        {/* Right account panel */}
        <div className="profile-content-panel">
          {/* Avatar + name/email */}
          <div className="profile-identity-row">
            <div className="profile-avatar-circle">
              {photoURL ? (
                <img src={photoURL} alt="User Profile" />
              ) : (
                <span className="profile-avatar-initial">{avatarInitial}</span>
              )}
            </div>
            <div className="profile-identity-text">
              <h2 className="profile-display-name">{displayName}</h2>
              <p className="profile-email-address">{email}</p>
            </div>
          </div>

          {/* Template usage / upgrade banner */}
          <div className="profile-usage-banner">
            <div className="usage-banner-icon">
              <img src={diamond} alt="" />
            </div>
            <div className="usage-banner-text">
              {isPremium ? (
                <>
                  <p className="usage-banner-title">Your Premium Plan is active!</p>
                  <p className="usage-banner-subtitle">
                    Enjoy unlimited templates and all features.
                    {renewalDate && formatRenewalDate(renewalDate) && (
                      <> Renews on {formatRenewalDate(renewalDate)}.</>
                    )}
                  </p>
                </>
              ) : (
                <>
                  <p className="usage-banner-title">You are using 1 Template</p>
                  <p className="usage-banner-subtitle">
                    Unlock more templates and premium features.
                  </p>
                </>
              )}
            </div>
            {!isPremium && (
              <button className="usage-upgrade-btn" onClick={() => setShowGateDialog(true)}>
                Upgrade Now <span aria-hidden="true">&rarr;</span>
              </button>
            )}
          </div>

          {/* Action Options List */}
          <div className="profile-action-menu-list">
            {/* Cancel Subscription Option (Only shown if user has active premium status) */}
            {isPremium && (
              <div className="action-menu-item-row" onClick={handleCancelSubscriptionClick}>
                <div className="action-item-icon-box orange-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
                    <line x1="2" y1="10" x2="22" y2="10" />
                  </svg>
                </div>
                <div className="action-item-text-group">
                  <span className="action-item-text-title">Cancel Subscription</span>
                  <span className="action-item-text-subtitle">Cancel your active premium plan</span>
                </div>
                <div className="action-item-arrow">
                  <svg width="6" height="10" viewBox="0 0 6 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 9L5 5L1 1" />
                  </svg>
                </div>
              </div>
            )}

            {/* Logout Option */}
            <div className="action-menu-item-row" onClick={handleLogoutClick}>
              <div className="action-item-icon-box green-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </div>
              <div className="action-item-text-group">
                <span className="action-item-text-title">Logout</span>
                <span className="action-item-text-subtitle">Sign out from your account</span>
              </div>
              <div className="action-item-arrow">
                <svg width="6" height="10" viewBox="0 0 6 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 9L5 5L1 1" />
                </svg>
              </div>
            </div>

            {/* Deactivate Option */}
            <div className="action-menu-item-row" onClick={() => setShowDeactivateConfirm(true)}>
              <div className="action-item-icon-box purple-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <line x1="17" y1="8" x2="22" y2="13" />
                  <line x1="22" y1="8" x2="17" y2="13" />
                </svg>
              </div>
              <div className="action-item-text-group">
                <span className="action-item-text-title">Deactivate Account</span>
                <span className="action-item-text-subtitle">Temporarily disable your account</span>
              </div>
              <div className="action-item-arrow">
                <svg width="6" height="10" viewBox="0 0 6 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 9L5 5L1 1" />
                </svg>
              </div>
            </div>

            {/* Delete Option */}
            <div className="action-menu-item-row" onClick={() => setShowDeleteConfirm(true)}>
              <div className="action-item-icon-box red-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </div>
              <div className="action-item-text-group">
                <span className="action-item-text-title">Delete Account</span>
                <span className="action-item-text-subtitle">Permanently delete your account and all data</span>
              </div>
              <div className="action-item-arrow">
                <svg width="6" height="10" viewBox="0 0 6 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 9L5 5L1 1" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Premium Upgrading Checkout Drawer */}
      <PremiumGateDialog
        open={showGateDialog}
        onClose={() => setShowGateDialog(false)}
      />

      {/* Cancel Subscription Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelConfirm}
        icon={<div className="cancel-color">💳</div>}
        title="Cancel Premium Plan"
        message="Are you sure you want to cancel your premium subscription? Your pro features will remain active until the end of the current billing cycle."
        cancelText="No, Keep It"
        confirmText="Yes, Cancel Plan"
        confirmLoading={cancelLoading}
        onCancel={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelSubscriptionConfirm}
      />

      {/* Cancel Subscription Admin Alert Dialog */}
      <ConfirmDialog
        open={showCancelAdminInfo}
        icon={<div className="admin-color">⚙️</div>}
        title="Manage Subscription"
        message="Your premium subscription was assigned by an Administrator. Please contact Whizpool Support or Admin to modify or cancel your subscription."
        cancelText="Dismiss"
        onCancel={() => setShowCancelAdminInfo(false)}
        hideCancel={true}
      />

      {/* Cancel Subscription Mobile App Store Info Dialog */}
      <ConfirmDialog
        open={showCancelMobileInfo}
        icon={<div className="store-color">📱</div>}
        title="Manage Subscription"
        message="Your premium subscription was purchased via mobile app store (RevenueCat). Please cancel it through your device settings: Google Play Store (Android) or Apple App Store (iOS)."
        cancelText="Dismiss"
        onCancel={() => setShowCancelMobileInfo(false)}
        hideCancel={true}
      />

      {/* Deactivate Account Confirmation Dialog */}
      <ConfirmDialog
        open={showDeactivateConfirm}
        icon={<div className="deactivate-color">⚠️</div>}
        title="Deactivate Account"
        message="Are you sure you want to deactivate your account? You will be signed out and will need to contact support to reactivate your access."
        cancelText="Cancel"
        confirmText="Deactivate"
        onCancel={() => setShowDeactivateConfirm(false)}
        onConfirm={handleDeactivateConfirm}
      />

      {/* Delete Account Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        icon={<div className="delete-color">🚨</div>}
        title="Delete Account Permanently"
        message="This action is irreversible. All your saved templates, signatures, and subscription privileges will be permanently deleted."
        cancelText="Cancel"
        confirmText="Delete"
        confirmLoading={deleteLoading}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
      />
      </div>
    </>
  );
};

export default ProfilePage;
